import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Form } from "../../compat";
import { registerOverlayClose } from "../../overlayStore";
import { clsx } from "../../utils/clsx";
import Button from "../Button/Button";
import Field from "../Field/Field";
import Input from "../Input/Input";
import Popover from "../Popover/Popover";
import "./WorthyCalendar.css";

/** Utils */
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysInMonth = (y, m) => [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

const PRECISION_LABEL_IDS = {
  year: "worthy_calendar_precision_year",
  month: "worthy_calendar_precision_month",
  date: "worthy_calendar_precision_date",
};

const PRECISION_DEFAULT_MESSAGES = {
  [PRECISION_LABEL_IDS.year]: "Year",
  [PRECISION_LABEL_IDS.month]: "Month",
  [PRECISION_LABEL_IDS.date]: "Date",
};

const defaultDateValue = (precision = "date") => ({
  year: null,
  month: null,
  day: null,
  hour: null,
  minute: null,
  precision,
});

const isCalendarValueObject = (value) =>
  value &&
  typeof value === "object" &&
  ("year" in value || "month" in value || "day" in value || "precision" in value || "hour" in value || "minute" in value);

const normalizeCalendarValue = (value, { showDate, showTime }) => {
  const base = defaultDateValue(showDate ? "date" : showTime ? "time" : "date");
  if (value == null || value === "") {
    return base;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return base;

    const result = { ...base };
    const sanitized = trimmed.replace("T", " ");
    const parts = sanitized.split(/\s+/).filter(Boolean);

    let datePart = null;
    let timePart = null;

    if (showDate) {
      datePart = parts[0] || null;
      if (showTime) {
        if (parts.length > 1) {
          timePart = parts[1];
        } else if (parts.length === 1 && parts[0] && parts[0].includes(":")) {
          // Only time segment present
          timePart = parts[0];
          datePart = null;
        }
      }
    } else if (showTime) {
      timePart = parts[0] || null;
    }

    if (datePart) {
      const [y, m, d] = datePart.split("-");
      const year = Number.parseInt(y, 10);
      const month = m != null ? Number.parseInt(m, 10) : null;
      const day = d != null ? Number.parseInt(d, 10) : null;

      if (!Number.isNaN(year)) {
        result.year = year;
        if (d && !Number.isNaN(day)) {
          result.day = clamp(day, 1, daysInMonth(year, clamp(month ?? 1, 1, 12)));
          result.month = !Number.isNaN(month) ? clamp(month, 1, 12) : result.month;
          result.precision = "date";
        } else if (m && !Number.isNaN(month)) {
          result.month = clamp(month, 1, 12);
          result.day = null;
          result.precision = "month";
        } else {
          result.month = null;
          result.day = null;
          result.precision = "year";
        }
      }
    }

    if (showTime && timePart) {
      const [hourStr, minuteStr] = timePart.split(":");
      const parsedHour = Number.parseInt(hourStr, 10);
      const parsedMinute = Number.parseInt(minuteStr, 10);
      if (!Number.isNaN(parsedHour)) {
        result.hour = clamp(parsedHour, 0, 23);
      }
      if (!Number.isNaN(parsedMinute)) {
        result.minute = clamp(parsedMinute, 0, 59);
      }
      if (!showDate) {
        result.precision = "time";
      }
    }

    return result;
  }

  if (isCalendarValueObject(value)) {
    return { ...base, ...value };
  }

  return base;
};

const formatCalendarValue = (value, { showDate, showTime }) => {
  if (!value) return "";

  if (!showDate && showTime) {
    return formatTimeValue(value) ?? "";
  }

  if (!showDate) return "";

  if (showTime && value.precision === "date") {
    const iso = toISO(value);
    return iso ?? "";
  }

  const isoWithoutTime = toISO(value, { includeTime: false });
  return isoWithoutTime ?? "";
};

const padTime = (value) => String(value ?? "").padStart(2, "0");

const formatTimeValue = (value) => {
  if (value?.hour == null || value?.minute == null) return null;
  return `${padTime(value.hour)}:${padTime(value.minute)}`;
};

const getPrecisionLabel = (intl, precision) => {
  const id = PRECISION_LABEL_IDS[precision] || PRECISION_LABEL_IDS.date;
  return intl.formatMessage({ id, defaultMessage: PRECISION_DEFAULT_MESSAGES[id] });
};

function monthLabel(month, locale = "en") {
  return new Date(2000, month - 1, 1).toLocaleString(locale, { month: "long" });
}

function weekdayLabels(locale = "en", weekStartsOn = 1, compact = false) {
  const base = new Date(2000, 0, 2);
  const full = range(0, 6).map((i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const label = d
      .toLocaleDateString(locale, { weekday: compact ? "narrow" : "short" })
      .replace(".", "");
    return compact ? label.slice(0, 1) : label;
  });
  return weekStartsOn === 1 ? [...full.slice(1), full[0]] : full;
}

function toISO(value, { includeTime = true } = {}) {
  if (!value) return null;
  const hasDate = value.year != null;
  const timeSegment =
    includeTime && value.hour != null && value.minute != null
      ? `${padTime(value.hour)}:${padTime(value.minute)}`
      : null;

  if (!hasDate) {
    return timeSegment;
  }

  const y = String(value.year).padStart(4, "0");
  if (value.precision === "year") return y;
  const m = String(value.month ?? 1).padStart(2, "0");
  if (value.precision === "month") return `${y}-${m}`;
  const d = String(value.day ?? 1).padStart(2, "0");
  const iso = `${y}-${m}-${d}`;
  if (!timeSegment) return iso;
  return `${iso}T${timeSegment}`;
}

/** Calendar (inline card) */
export function WorthyCalendar({
  value: valueProp,
  onChange,
  locale,
  weekStartsOn = 1,
  minYear,
  maxYear,
  minAge,
  maxAge,
  className = "",
  compactWeekdays = false,
  showDate = true,
  showTime = false,
}) {
  const showDatePicker = showDate !== false;
  const showTimePicker = showTime === true;
  const intl = useIntl();
  const calendarLocale = locale || intl.locale || "en";
  const now = useMemo(() => new Date(), []);
  const value = useMemo(
    () => normalizeCalendarValue(valueProp, { showDate: showDatePicker, showTime: showTimePicker }),
    [showDatePicker, showTimePicker, valueProp],
  );
  const initialBounds = useMemo(() => {
    const min =
      minYear != null ? minYear : minAge != null ? now.getFullYear() - (maxAge ?? 150) : 1600;
    const max =
      maxYear != null ? maxYear : maxAge != null ? now.getFullYear() - maxAge : now.getFullYear();
    return { min, max };
  }, [minYear, minAge, maxAge, maxYear, now]);
  const [view, setView] = useState(() => {
    if (!showDatePicker) return null;
    if (!value.year) return 'year';
    if (!value.month) return 'month';
    return 'date';
  });
  const [cursor, setCursor] = useState(() => ({
    year: value.year ?? clamp(now.getFullYear(), initialBounds.min, initialBounds.max),
    month: value.month ?? now.getMonth() + 1,
  }));

  const emitChange = useCallback(
    (next) => {
      if (!onChange) return;
      const formatted = formatCalendarValue(next, { showDate: showDatePicker, showTime: showTimePicker });
      onChange(formatted);
    },
    [onChange, showDatePicker, showTimePicker],
  );

  useEffect(() => {
    if (!showDatePicker) return;
    const nextView = !value.year ? 'year' : !value.month ? 'month' : 'date';
    setView(nextView);
    setCursor((prev) => ({
      year: value.year ?? prev.year,
      month: value.month ?? prev.month,
    }));
  }, [showDatePicker, value.month, value.year]);

  const computedMinYear = useMemo(() => {
    if (minYear != null) return minYear;
    if (minAge != null) return now.getFullYear() - (maxAge ?? 150);
    return 1600;
  }, [minYear, minAge, maxAge, now]);

  const computedMaxYear = useMemo(() => {
    if (maxYear != null) return maxYear;
    if (maxAge != null) return now.getFullYear() - maxAge;
    return now.getFullYear();
  }, [maxYear, maxAge, now]);

  useEffect(() => {
    if (!showDatePicker) return;
    if (view === "month" && !value.year) emitChange({ ...value, year: cursor.year, precision: "month" });
    if (view === "date" && (!value.year || !value.month)) {
      const todayInView = clamp(now.getDate(), 1, daysInMonth(cursor.year, cursor.month));
      emitChange({ year: cursor.year, month: cursor.month, day: value.day ?? todayInView, precision: "date" });
    }
  }, [cursor.month, cursor.year, emitChange, showDatePicker, value.day, value.month, value.year, view]);

  const labelId = useId();
  const timeHeadingId = useId();
  const hourColumnId = useId();
  const minuteColumnId = useId();

  const precisionOptions = useMemo(() => {
    if (!showDatePicker) return [];
    return [
      { key: "year", label: getPrecisionLabel(intl, "year") },
      { key: "month", label: getPrecisionLabel(intl, "month") },
      { key: "date", label: getPrecisionLabel(intl, "date") },
    ];
  }, [intl, showDatePicker]);

  const precisionHeading = showDatePicker
    ? intl.formatMessage({
        id: "worthy_calendar_precision_heading",
        defaultMessage: "Choose date precision",
      })
    : null;

  const noSelectionMessage = showTimePicker && !showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_unset_time", defaultMessage: "No time selected" })
    : intl.formatMessage({ id: "worthy_calendar_unset", defaultMessage: "No date selected" });

  const hourLabel = intl.formatMessage({ id: "designSystem.timePicker.label.hour", defaultMessage: "Hour" });
  const minuteLabel = intl.formatMessage({ id: "designSystem.timePicker.label.minute", defaultMessage: "Minute" });
  const timeHeading = intl.formatMessage({ id: "worthy_calendar_time_heading", defaultMessage: "Choose time" });

  const selectedSummary = useMemo(() => {
    const hasDate = value.year != null;
    const timeValue = formatTimeValue(value);

    if (!hasDate && !timeValue) {
      return noSelectionMessage;
    }

    if (!hasDate && timeValue) {
      return timeValue;
    }

    const isoWithoutTime = toISO(value, { includeTime: false });
    const isoValue = toISO(value);

    if (value.precision && value.precision !== "date") {
      if (!isoWithoutTime) return noSelectionMessage;
      const base = intl.formatMessage(
        { id: "worthy_calendar_selection_value", defaultMessage: "{iso} ({precision})" },
        { iso: isoWithoutTime, precision: getPrecisionLabel(intl, value.precision) },
      );
      return timeValue ? `${base} • ${timeValue}` : base;
    }

    const formatted = isoValue ? isoValue.replace("T", " ") : isoWithoutTime;
    return formatted || noSelectionMessage;
  }, [intl, noSelectionMessage, value]);

  const monthNavLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_select_month", defaultMessage: "Choose month" })
    : "";
  const yearNavLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_select_year", defaultMessage: "Choose year" })
    : "";
  const previousLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_previous", defaultMessage: "Previous" })
    : "";
  const nextLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_next", defaultMessage: "Next" })
    : "";
  const yearGridLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_select_year", defaultMessage: "Choose year" })
    : "";
  const monthGridLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_select_month", defaultMessage: "Choose month" })
    : "";
  const dayGridLabel = showDatePicker
    ? intl.formatMessage({ id: "worthy_calendar_select_day", defaultMessage: "Choose day" })
    : "";
  const selectedLabel = intl.formatMessage({ id: "worthy_calendar_selected_label", defaultMessage: "Selected:" });
  const rangeLabel = showDatePicker
    ? intl.formatMessage(
        { id: "worthy_calendar_valid_years", defaultMessage: "Valid years: {min}–{max}" },
        { min: computedMinYear, max: computedMaxYear },
      )
    : null;

  const setPrecision = (precision) => {
    if (!showDatePicker) return;
    setView(precision);
    emitChange({ ...value, precision });
  };

  const pickYear = (year) => {
    if (!showDatePicker) return;
    const next = { ...value, year, precision: 'year' };
    if (next.month && next.day) next.day = clamp(next.day, 1, daysInMonth(year, next.month));
    emitChange(next);
    setCursor((c) => ({ ...c, year }));
    // Always advance to month selection after picking a year
    setView("month");
  };

  const pickMonth = (month) => {
    if (!showDatePicker) return;
    if (!value.year) return;
    const next = { ...value, month, precision: 'month' };
    if (next.day) next.day = clamp(next.day, 1, daysInMonth(value.year, month));
    emitChange(next);
    setCursor((c) => ({ ...c, month }));
    // Always advance to day selection after picking a month
    setView("date");
  };

  const pickDay = (day) => {
    if (!showDatePicker) return;
    if (!cursor.year || !cursor.month) return;
    emitChange({ year: cursor.year, month: cursor.month, day, precision: "date" });
  };

  const ensureTimePrecision = (next) => {
    if (showDatePicker) return next;
    return { ...next, precision: "time" };
  };

  const pickHour = (hour) => {
    if (!showTimePicker) return;
    const nextMinute = value.minute ?? 0;
    const next = ensureTimePrecision({ ...value, hour, minute: nextMinute });
    emitChange(next);
  };

  const pickMinute = (minute) => {
    if (!showTimePicker) return;
    const nextHour = value.hour ?? 0;
    const next = ensureTimePrecision({ ...value, hour: nextHour, minute });
    emitChange(next);
  };

  const start = Math.floor(cursor.year / 12) * 12;
  const years = range(start, start + 11);
  const months = range(1, 12);
  const hours = useMemo(() => range(0, 23), []);
  const minutes = useMemo(() => range(0, 59), []);
  const first = new Date(cursor.year, cursor.month - 1, 1);
  let startIdx = (first.getDay() + 6) % 7; // Monday = 0
  if (weekStartsOn === 0) startIdx = first.getDay();
  const hasExplicitDate = Boolean(value.year && value.month && value.day);

  const datePanel = showDatePicker ? (
    <div className="wc-datePanel">
      <div className="wc-row wc-space">
        <h3 id={labelId} className="wc-title">
          {precisionHeading}
        </h3>
        <div className="wc-chip-group" role="tablist" aria-labelledby={labelId}>
          {precisionOptions.map(({ key, label }) => (
            <Button
              key={key}
              role="tab"
              aria-selected={view === key}
              onClick={() => setPrecision(key)}
              size="sm"
              shape="round"
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="wc-row wc-space" aria-live="polite">
        <div className="wc-subtle">
          {view === "date" && value.year && (
            <Button onClick={() => setView("month")} aria-label={monthNavLabel} size="sm">
              {monthLabel(cursor.month, calendarLocale)} {cursor.year}
            </Button>
          )}
          {view === "month" && (
            <Button onClick={() => setView("year")} aria-label={yearNavLabel} size="sm">
              {cursor.year}
            </Button>
          )}
          {view === "year" && (
            <span className="wc-range">
              {Math.floor(cursor.year / 12) * 12}–{Math.floor(cursor.year / 12) * 12 + 11}
            </span>
          )}
        </div>
        <div className="wc-row">
          <Button
            aria-label={previousLabel}
            onClick={() => {
              if (view === "date") {
                let m = cursor.month - 1;
                let y = cursor.year;
                if (m < 1) {
                  m = 12;
                  y -= 1;
                }
                setCursor({ year: y, month: m });
              } else if (view === "month") {
                setCursor((c) => ({ ...c, year: c.year - 1 }));
              } else {
                setCursor((c) => ({ ...c, year: c.year - 12 }));
              }
            }}
            size="sm"
            shape="circle"
          >
            {ChevronLeft()}
          </Button>
          <Button
            aria-label={nextLabel}
            onClick={() => {
              if (view === "date") {
                let m = cursor.month + 1;
                let y = cursor.year;
                if (m > 12) {
                  m = 1;
                  y += 1;
                }
                setCursor({ year: y, month: m });
              } else if (view === "month") {
                setCursor((c) => ({ ...c, year: c.year + 1 }));
              } else {
                setCursor((c) => ({ ...c, year: c.year + 12 }));
              }
            }}
            size="sm"
            shape="circle"
          >
            {ChevronRight()}
          </Button>
        </div>
      </div>

      {view === "year" && (
        <div className="wc-grid wc-grid--years" role="grid" aria-label={yearGridLabel}>
          {years.map((year) => {
            const disabled = year < computedMinYear || year > computedMaxYear;
            const selected = value.year === year;
            return (
              <Button
                key={year}
                role="gridcell"
                aria-selected={selected}
                disabled={disabled}
                onClick={() => pickYear(year)}
                size="sm"
              >
                {year}
              </Button>
            );
          })}
        </div>
      )}

      {view === "month" && (
        <div className="wc-grid wc-grid--months" role="grid" aria-label={monthGridLabel}>
          {months.map((month) => {
            const selected = value.month === month && value.year === cursor.year;
            return (
              <Button
                key={month}
                role="gridcell"
                aria-selected={selected}
                disabled={!value.year}
                onClick={() => pickMonth(month)}
                size="sm"
              >
                {monthLabel(month, calendarLocale)}
              </Button>
            );
          })}
        </div>
      )}

      {view === "date" && value.year && cursor.month && (
        <div>
          <div className="wc-weekheads">
            {weekdayLabels(calendarLocale, weekStartsOn, compactWeekdays).map((weekday) => (
              <div key={weekday} className="wc-weekhead">
                {weekday}
              </div>
            ))}
          </div>
          <div className="wc-grid wc-grid--days" role="grid" aria-label={dayGridLabel}>
            {(() => {
              const dim = daysInMonth(cursor.year, cursor.month);
              const days = range(1, dim);
              const cells = Array.from({ length: startIdx }, () => null).concat(days);
              while (cells.length % 7 !== 0) cells.push(null);

              return cells.map((day, index) => {
                const selected =
                  day === value.day && cursor.month === value.month && value.year === cursor.year;
                if (day == null) {
                  return <div key={`blank-${index}`} className="wc-empty" aria-hidden="true" />;
                }
                const isToday =
                  day === now.getDate() &&
                  cursor.month === now.getMonth() + 1 &&
                  cursor.year === now.getFullYear();
                const highlight = selected || (!hasExplicitDate && isToday);
                return (
                  <Button
                    key={index}
                    role="gridcell"
                    aria-selected={highlight}
                    aria-current={isToday ? "date" : undefined}
                    onClick={() => pickDay(day)}
                    size="sm"
                    variant={highlight ? "filled" : undefined}
                    color={highlight ? "primary" : undefined}
                    className={isToday ? "wc-day--today" : undefined}
                  >
                    {day}
                  </Button>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  ) : null;

  const timePanel = showTimePicker ? (
    <div className="wc-timePanel" aria-labelledby={timeHeadingId}>
      <div className="wc-timeHeader" id={timeHeadingId}>
        {timeHeading}
      </div>
      <div className="wc-timeColumns" role="group" aria-labelledby={timeHeadingId}>
        <div className="wc-timeColumn">
          <div className="wc-timeColumnLabel" id={hourColumnId}>
            {hourLabel}
          </div>
          <div className="wc-timeList" role="listbox" aria-labelledby={hourColumnId}>
            {hours.map((hour) => {
              const selected = value.hour === hour;
              return (
                <Button
                  key={hour}
                  size="sm"
                  variant={selected ? "filled" : "text"}
                  color={selected ? "primary" : "default"}
                  className="wc-timeButton"
                  onClick={() => pickHour(hour)}
                  aria-selected={selected}
                  aria-label={`${padTime(hour)} ${hourLabel}`}
                  role="option"
                >
                  {padTime(hour)}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="wc-timeColumn">
          <div className="wc-timeColumnLabel" id={minuteColumnId}>
            {minuteLabel}
          </div>
          <div className="wc-timeList" role="listbox" aria-labelledby={minuteColumnId}>
            {minutes.map((minute) => {
              const selected = value.minute === minute;
              return (
                <Button
                  key={minute}
                  size="sm"
                  variant={selected ? "filled" : "text"}
                  color={selected ? "primary" : "default"}
                  className="wc-timeButton"
                  onClick={() => pickMinute(minute)}
                  aria-selected={selected}
                  aria-label={`${padTime(minute)} ${minuteLabel}`}
                  role="option"
                >
                  {padTime(minute)}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const bodyClassName = clsx("wc-body", showDatePicker && showTimePicker && "wc-body--split");

  return (
    <div className={`wc-card ${className}`}>
      <div className={bodyClassName}>
        {datePanel}
        {timePanel}
      </div>

      <div className="wc-row wc-footer">
        <div>
          <div className="wc-muted">{selectedLabel}</div>
          <div className="wc-mono">{selectedSummary}</div>
        </div>
        {rangeLabel ? <div className="wc-subtle">{rangeLabel}</div> : null}
      </div>
    </div>
  );
}

/** Field wrapper: desktop popover + mobile bottom sheet */
function WorthyDateControl(props) {
  const {
    id,
    label,
    value,
    onChange,
    onBlur,
    onFocus,
    locale,
    weekStartsOn = 1,
    minYear,
    maxYear,
    minAge,
    maxAge,
    required,
    mobileFullScreen = true,
    showLabel = true,
    className,
    style,
    disabled,
    showErrors = true,
    showDate = true,
    showTime = false,
  } = props;

  const ariaInvalidProp = props["aria-invalid"];
  const ariaDescribedBy = props["aria-describedby"];

  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const generatedFieldId = useId();
  const fieldId = id ?? generatedFieldId;
  const popoverId = useId();
  const safeValue = useMemo(
    () => normalizeCalendarValue(value, { showDate, showTime }),
    [showDate, showTime, value],
  );

  const placeholder = showDate
    ? showTime
      ? intl.formatMessage({ id: "worthy_calendar_choose_datetime", defaultMessage: "Choose date & time" })
      : intl.formatMessage({ id: "worthy_calendar_choose_date", defaultMessage: "Choose date" })
    : intl.formatMessage({ id: "worthy_calendar_choose_time", defaultMessage: "Choose time" });

  const defaultLabelDescriptor = showDate
    ? showTime
      ? { id: "worthy_calendar_default_datetime_label", defaultMessage: "Event date & time" }
      : { id: "worthy_calendar_default_label", defaultMessage: "Event date" }
    : { id: "worthy_calendar_default_time_label", defaultMessage: "Event time" };

  const resolvedLabel = label ?? intl.formatMessage(defaultLabelDescriptor);

  const displayValue = useMemo(() => {
    const timeDisplay = formatTimeValue(safeValue);

    if (!showDate && showTime) {
      return timeDisplay ?? "";
    }

    if (showDate && safeValue.precision && safeValue.precision !== "date") {
      const isoWithoutTime = toISO(safeValue, { includeTime: false });
      if (!isoWithoutTime) return "";
      const base = intl.formatMessage(
        { id: "worthy_calendar_selection_value", defaultMessage: "{iso} ({precision})" },
        { iso: isoWithoutTime, precision: getPrecisionLabel(intl, safeValue.precision) },
      );
      return showTime && timeDisplay ? `${base} • ${timeDisplay}` : base;
    }

    if (safeValue?.year && safeValue?.month && safeValue?.day) {
      const locale = props.locale || intl.locale || "en";
      const localized = new Date(
        safeValue.year,
        safeValue.month - 1,
        safeValue.day
      ).toLocaleDateString(locale);
      return showTime && timeDisplay ? `${localized} ${timeDisplay}` : localized;
    }

    return formatCalendarValue(safeValue, { showDate, showTime });
  }, [intl, props.locale, safeValue, showDate, showTime]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply);
    };
  }, []);

  useEffect(() => registerOverlayClose(() => setOpen(false)), []);

  useEffect(() => {
    if (isMobile && open && mobileFullScreen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [isMobile, mobileFullScreen, open]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  const closeLabel = intl.formatMessage({ id: "worthy_calendar_close", defaultMessage: "Close" });
  const cancelLabel = intl.formatMessage({ id: "worthy_calendar_cancel", defaultMessage: "Cancel" });
  const applyLabel = intl.formatMessage({ id: "worthy_calendar_apply", defaultMessage: "Apply" });
  const sheetTitle = intl.formatMessage({ id: "worthy_calendar_choose_date", defaultMessage: "Choose date" });

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleOpen();
    }
    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const statusContext = Form?.Item?.useStatus ? Form.Item.useStatus() : { status: undefined, errors: [] };
  const hasStatusError = showErrors && (statusContext?.status === "error" || (statusContext?.errors?.length ?? 0) > 0);
  const hasExternalErrorClass = showErrors && typeof className === "string" && className.split(/\s+/).includes("ds-form-control--error");
  const ariaInvalidFlag = ariaInvalidProp === true || ariaInvalidProp === "true";
  const hasError = Boolean(hasStatusError || hasExternalErrorClass || (showErrors && ariaInvalidFlag));

  const containerClassName = clsx(
    "wc-field",
    className,
    showErrors && hasError && "wc-field--error",
    !showErrors && "wc-field--noerror",
  );
  const inputWrapperClassName = clsx(
    "wc-inputWrapper",
    open && "wc-inputWrapper--open",
    showErrors && hasError && "wc-inputWrapper--error",
  );
  const inputClassName = clsx("wc-triggerInput", showErrors && hasError && "ds-form-control--error");

  const handleInputFocus = (event) => {
    if (!disabled) {
      setOpen(true);
    }
    onFocus?.(event);
  };

  const handleValueChange = useCallback(
    (nextValue) => {
      if (!onChange) return;
      // String payloads are already formatted (e.g., YYYY-MM-DD)
      if (typeof nextValue === "string") {
        onChange(nextValue);
        // Auto-close after full date is selected when not using time
        if (showDate && !showTime && /^\d{4}-\d{2}-\d{2}$/.test(nextValue)) {
          setOpen(false);
        }
        return;
      }

      const normalized = normalizeCalendarValue(nextValue, { showDate, showTime });
      const formatted = formatCalendarValue(normalized, { showDate, showTime });
      onChange(formatted);
      if (showDate && !showTime && /^\d{4}-\d{2}-\d{2}$/.test(formatted)) {
        setOpen(false);
      }
    },
    [onChange, showDate, showTime],
  );

  const inputElement = (
    <div className={inputWrapperClassName}>
      <Input
        id={fieldId}
        readOnly
        value={displayValue}
        placeholder={placeholder}
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={!isMobile && open ? popoverId : undefined}
        aria-invalid={ariaInvalidProp ?? (hasError ? true : undefined)}
        aria-describedby={ariaDescribedBy}
        onClick={disabled ? undefined : () => setOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        onFocus={handleInputFocus}
        className={inputClassName}
        disabled={disabled}
      />
      <span className="wc-triggerIcon" aria-hidden>
        {CalendarIcon()}
      </span>
    </div>
  );

  const handlePopoverOpenChange = (nextOpen) => {
    if (disabled && nextOpen) return;
    setOpen(nextOpen);
  };

  const desktopTrigger = (
    <Popover
      trigger={[]}
      arrow={false}
      placement="bottomLeft"
      destroyOnHidden
      closeOnBlur={false}
      className="wc-popoverHost"
      style={{ width: "100%" }}
      open={!isMobile && open && !disabled}
      onOpenChange={handlePopoverOpenChange}
      content={
        // <div className="wc-popoverContent" id={popoverId}>
          <WorthyCalendar
          id={popoverId}
            value={safeValue}
            onChange={handleValueChange}
            locale={locale || intl.locale}
            weekStartsOn={weekStartsOn}
            minYear={minYear}
            maxYear={maxYear}
            minAge={minAge}
            maxAge={maxAge}
            className="wc-shadow"
            showDate={showDate}
            showTime={showTime}
          />
      }
    >
      {inputElement}
    </Popover>
  );

  const triggerElement = !isMobile ? desktopTrigger : inputElement;

  return (
    <div className={containerClassName} style={style}>
      {showLabel ? (
        <Field label={resolvedLabel} htmlFor={fieldId} required={required}>
          {triggerElement}
        </Field>
      ) : (
        triggerElement
      )}

      {isMobile && open && !disabled && (
        <div className="wc-sheetOverlay" role="dialog" aria-modal="true">
          <div className="wc-sheet">
            <div className="wc-sheetGrab" />

            <div className="wc-sheetHeader">
              <div className="wc-sheetTitle">{sheetTitle}</div>
              <Button size="sm" shape="circle" aria-label={closeLabel} onClick={() => setOpen(false)}>
                {CloseIcon()}
              </Button>
            </div>

            <div className="wc-sheetBody">
              <WorthyCalendar
                value={safeValue}
                onChange={handleValueChange}
                locale={locale || intl.locale}
                weekStartsOn={weekStartsOn}
                minYear={minYear}
                maxYear={maxYear}
                minAge={minAge}
                maxAge={maxAge}
                className="wc-noBorder"
                compactWeekdays
                showDate={showDate}
                showTime={showTime}
              />
            </div>

            <div className="wc-sheetFooter">
              <Button type="button" onClick={() => setOpen(false)}>
                {cancelLabel}
              </Button>
              <Button type="button" onClick={() => setOpen(false)}>
                {applyLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function WorthyDateField({
  name,
  rules,
  formItemProps = {},
  required,
  label,
  className,
  style,
  id,
  showErrors = true,
  ...rest
}) {
  const intl = useIntl();
  const generatedId = useId();
  const { showDate: fieldShowDate = true, showTime: fieldShowTime = false } = rest;

  const {
    name: formItemName,
    label: formItemLabel,
    rules: formItemRules,
    required: formItemRequired,
    htmlFor: formItemHtmlFor,
    help: formItemHelp,
    ...restFormItemProps
  } = formItemProps;

  const mergedName = formItemName ?? name;
  const mergedRequired = formItemRequired ?? required ?? false;
  const resolvedId = id ?? formItemHtmlFor ?? generatedId;
  const defaultLabel = label ?? intl.formatMessage({ id: "worthy_calendar_default_label", defaultMessage: "Event date" });
  const mergedLabel = formItemLabel ?? defaultLabel;

  let mergedRules = [...(formItemRules || []), ...(rules || [])];
  if (mergedRequired) {
    const requiredDescriptor = fieldShowDate
      ? fieldShowTime
        ? { id: "worthy_calendar_required_datetime", defaultMessage: "Please select a date and time" }
        : { id: "worthy_calendar_required", defaultMessage: "Please select a date" }
      : { id: "worthy_calendar_required_time", defaultMessage: "Please select a time" };
    const requiredMessage = intl.formatMessage(requiredDescriptor);
    const hasRequiredRule = mergedRules.some((rule) => rule && rule.required);
    if (hasRequiredRule) {
      mergedRules = mergedRules.map((rule) => {
        if (rule && rule.required && !rule.message) {
          return { ...rule, message: requiredMessage };
        }
        return rule;
      });
    } else {
      mergedRules = [{ required: true, message: requiredMessage }, ...mergedRules];
    }
  }

  if (mergedName != null) {
    if (!showErrors) {
      return (
        <Form.Item
          name={mergedName}
          required={mergedRequired}
          rules={mergedRules}
          htmlFor={formItemHtmlFor ?? resolvedId}
          noStyle
          {...restFormItemProps}
        >
          <WorthyDateControl
            {...rest}
            id={resolvedId}
            label={mergedLabel}
            required={mergedRequired}
            className={className}
            style={style}
            showLabel
            showErrors={false}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        name={mergedName}
        label={mergedLabel}
        required={mergedRequired}
        rules={mergedRules}
        htmlFor={formItemHtmlFor ?? resolvedId}
        help={formItemHelp}
        {...restFormItemProps}
      >
        <WorthyDateControl
          {...rest}
          id={resolvedId}
          label={mergedLabel}
          required={mergedRequired}
          className={className}
          style={style}
          showLabel={false}
          showErrors
        />
      </Form.Item>
    );
  }

  return (
    <WorthyDateControl
      {...rest}
      id={resolvedId}
      label={defaultLabel}
      required={mergedRequired}
      className={className}
      style={style}
      showLabel
      showErrors={showErrors}
    />
  );
}

/** Icons (inline) */
function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** (Optional) Demo component can be omitted in production */
export default function WorthyCalendarDemo() {
  const [value, setValue] = useState(() => "");
  const [yOnly, setYOnly] = useState(() => "1934");
  const [timeOnly, setTimeOnly] = useState(() => "");
  return (
    <div style={{ maxWidth: "768px", margin: "0 auto", padding: "24px" }}>
      <h2 style={{ fontSize: "18px", fontWeight: 600, color: "var(--text)" }}>WorthyCalendar – Demo (no Tailwind)</h2>
      <div className="wc-card" style={{ marginTop: 12 }}>
        <WorthyCalendar
          value={value}
          onChange={setValue}
          minYear={1600}
          maxYear={new Date().getFullYear()}
          weekStartsOn={1}
          showTime
        />
        <div className="wc-row wc-footer" style={{ marginTop: 12 }}>
          <div>
            <div className="wc-muted">ISO:</div>
            <div className="wc-mono">{value || "—"}</div>
          </div>
          <div className="wc-subtle">State: {JSON.stringify(value)}</div>
        </div>
      </div>
      <div className="wc-row" style={{ marginTop: 16, gap: "16px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "220px" }}>
          <WorthyDateField label="Dato for hendelsen" value={value} onChange={setValue} required showTime />
        </div>
        <div style={{ flex: 1, minWidth: "220px" }}>
          <WorthyDateField label="År (kun)" value={yOnly} onChange={setYOnly} />
        </div>
        <div style={{ flex: 1, minWidth: "220px" }}>
          <WorthyDateField label="Tidspunkt" value={timeOnly} onChange={setTimeOnly} showDate={false} showTime />
        </div>
      </div>
    </div>
  );
}
